import React, { useState } from 'react';
import QUESTS from './quests.json';
import { SIZES, VIBES } from './meta';

const sizeOf = (id) => SIZES.find((s) => s.id === id);
const vibeOf = (id) => VIBES.find((v) => v.id === id);

// the site a quest's link goes to ("www.nps.gov" -> "nps.gov")
export const hostOf = (url) => new URL(url).hostname.replace(/^www\./, '');

// the picture from the quest's source page, or a plain tile with the kind's icon when there is none
export const questImage = (quest) => (quest.image ? `${process.env.PUBLIC_URL || ''}/quests/${quest.image}` : null);

function QuestPicture({ quest, className }) {
  const src = questImage(quest);
  if (src) return <img className={className} src={src} alt="" loading="lazy" />;
  return <span className={`${className} fm-quest-blank`} data-vibe={quest.vibe} aria-hidden="true">{vibeOf(quest.vibe).icon}</span>;
}

// the quests that match the chosen size and vibe ('any' matches everything)
export function filterQuests(quests, size, vibe) {
  return quests.filter((q) => (size === 'any' || q.size === size) && (vibe === 'any' || q.vibe === vibe));
}

// a random quest other than `current`, or null when there is nothing to pick
export function rollQuest(quests, current, rng = Math.random) {
  const pool = quests.filter((q) => !current || q.id !== current.id);
  if (!pool.length) return quests[0] || null;
  return pool[Math.floor(rng() * pool.length)];
}

function QuestBoard() {
  const [size, setSize] = useState('any');
  const [vibe, setVibe] = useState('any');
  const [quest, setQuest] = useState(() => rollQuest(QUESTS, null));

  const matches = filterQuests(QUESTS, size, vibe);
  // a filter that no longer fits the quest on show rolls a new one
  const pick = (nextSize, nextVibe) => {
    const pool = filterQuests(QUESTS, nextSize, nextVibe);
    if (!quest || !pool.some((q) => q.id === quest.id)) setQuest(rollQuest(pool, null));
  };

  return (
    <div className="fm-quest">
      <div className="fm-quest-main">
        <button type="button" className="ms-btn fm-quest-roll" disabled={!matches.length} onClick={() => setQuest(rollQuest(matches, quest))}>🎲 Roll a quest</button>
      <div className="fm-quest-filter" role="group" aria-label="Size">
        <span className="fm-quest-label">size</span>
        {[{ id: 'any', label: 'any' }, ...SIZES.map((s) => ({ id: s.id, label: s.id }))].map((s) => (
          <button key={s.id} type="button" className="ms-btn ms-btn-small" aria-pressed={s.id === size} onClick={() => { setSize(s.id); pick(s.id, vibe); }}>{s.label}</button>
        ))}
      </div>
      <div className="fm-quest-filter" role="group" aria-label="Kind">
        <span className="fm-quest-label">kind</span>
        {[{ id: 'any', label: 'any' }, ...VIBES].map((v) => (
          <button key={v.id} type="button" className="ms-btn ms-btn-small" aria-pressed={v.id === vibe} onClick={() => { setVibe(v.id); pick(size, v.id); }}>{v.id === 'fun' ? 'fun' : v.label}</button>
        ))}
      </div>

      {matches.length === 0 && <p className="fm-quest-none">Nothing matches. Try another filter.</p>}

      {quest && matches.length > 0 && (
        <article className="fm-quest-card" data-vibe={quest.vibe}>
          <QuestPicture quest={quest} className="fm-quest-photo" />
          <p className="fm-quest-kind">
            {sizeOf(quest.size).label} · {sizeOf(quest.size).hint} · {vibeOf(quest.vibe).label}
          </p>
          <h3 className="fm-quest-title">{quest.title}</h3>
          <p className="fm-quest-blurb">{quest.blurb}</p>
          {quest.url
            ? <a className="ms-btn fm-quest-go" href={quest.url} target="_blank" rel="noopener noreferrer">{hostOf(quest.url)} ↗</a>
            : <span className="ms-btn fm-quest-go" aria-disabled="true">no link saved</span>}
        </article>
      )}
      </div>

      {matches.length > 0 && (
        <div className="fm-quest-gridwrap">
          <ul className="fm-quest-grid" aria-label="Quests">
            {matches.map((q) => (
              <li key={q.id}>
                <button type="button" className="fm-quest-tile" data-vibe={q.vibe} aria-pressed={quest && q.id === quest.id} onClick={() => setQuest(q)} title={q.title}>
                  <QuestPicture quest={q} className="fm-quest-thumb" />
                  <span className="fm-quest-name">{q.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default QuestBoard;
